import React, { Fragment, useEffect } from 'react';
import { LoadingIndicator } from '@festo-ui/react';
import { FacetFilter } from 'festo-react-ui-kit';

import SolutionTile from '../../components/Solution/SolutionTile/SolutionTile';
import ResultsFilter from '../../components/Filter/ResultsFilter';
import { InfoPanel } from '../../components/InfoPanel';

import useTranslation from '../../features/translation/hooks/useTranslation';
import useURLParams from '../../hooks/useURLParams';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  selectedFilters,
  fetchResults,
  isResultsLoading,
  updateFilter
} from '../../features/filter/filterSlice';

import { checkFilterValue } from '../../utils';
import { econdaClickEvent } from '../../Econda';
import '../../index.scss';
import NotFound from './NotFound';
import { useLocation, useParams } from 'react-router';
import useSortBy from '../../hooks/useSortBy';
import { ChipContainerObject, FilterState } from '../../features/filter/interfaces';
import ChipContainer from '../../components/Filter/chip/container/Container';
import useFoxPresence from '../../components/Headline/hooks/useFoxPresence';
import useHeadline from '../../components/Headline/hooks/useHeadline';
import { RequestHandler } from '../../app/RequestHandler';
import usePricing from '../../features/pricing/hooks/usePricing';

enum TranslationKeys {
  SOLUTION = 'shockAbsorberSolution',
  FILTERS_TITLE = 'resultsFilter',
  SHOW_RESULTS = 'showResults',
}

const Results: React.FunctionComponent = () => {
  const dispatch = useAppDispatch();
  const t = useTranslation();
  const url = useParams();
  const { pathname } = useLocation();
  const { hasFoXHeadline } = useFoxPresence();
  const { headlineName } = useHeadline();
  const myFilters = useAppSelector(selectedFilters);
  const isLoading = useAppSelector(isResultsLoading);
  const shockAbsorbers = useSortBy({
    isLoading,
  });
  useURLParams(myFilters);

  const prepareChipsContainer = (filters: FilterState) => {
    const {
      recommendedResults,
      fixedStop,
      cushioningAdjustments,
      cushioningTechnologies,
      soft,
      robust,
      mountingType,
      forBatteryProduction,
    } = filters;

    const isCushioningReactionEmpty = soft === null && robust === null ? 'None' : '';
    const isSoft = soft ? `${t('filterCushioningReactionSoft')}` : '';
    const isHard = robust ? `${t('filterCushioningReactionRobust')}` : '';

    const isCushioningAdjustmentsEmpty =
      cushioningAdjustments[0].length === 0 && cushioningAdjustments[1].length === 0 ? 'None' : '';
    const isAutomatic =
      cushioningAdjustments[0] === 'AUTOMATIC' || cushioningAdjustments[1] === 'AUTOMATIC'
        ? `${t('filterCushioningAdjustmentAutomatic')}`
        : '';
    const isManual =
      cushioningAdjustments[0] === 'MANUAL' || cushioningAdjustments[1] === 'MANUAL'
        ? `${t('filterCushioningAdjustmentManual')}`
        : '';

    const isCushioningTechnologiesEmpty =
      cushioningTechnologies[0].length === 0 && cushioningTechnologies[1].length === 0 ? 'None' : '';
    const isHydraulic =
      cushioningTechnologies[0] === 'HYDRAULIC' || cushioningTechnologies[1] === 'HYDRAULIC'
        ? `${t('filterCushioningTechnologyHydraulic')}`
        : '';
    const isMechanical =
      cushioningTechnologies[0] === 'MECHANICAL' || cushioningTechnologies[1] === 'MECHANICAL'
        ? `${t('filterCushioningTechnologyMechanical')}`
        : '';

    const chipsContainer: ChipContainerObject = {
      filterFestoRecommendedResult: recommendedResults,
      filterFixedStop: fixedStop,
      filterCushioningReaction: `${isSoft} ${isSoft && isHard ? '|' : ''} ${isHard} ${isCushioningReactionEmpty}`,
      filterCushioningAdjustments: `${isAutomatic} ${
        isAutomatic && isManual ? '|' : ''
      } ${isManual} ${isCushioningAdjustmentsEmpty}`,
      filterCushioningTechnologies: `${isHydraulic} ${
        isHydraulic && isMechanical ? '|' : ''
      } ${isMechanical} ${isCushioningTechnologiesEmpty}`,
      filterMountingType: t(
        mountingType === 'LOCK_NUT' ? 'filterMountingTypeLockNut' : 'filterMountingTypeThreadedSleeve',
      ),
      filterAdditionalFunction: forBatteryProduction,
    };

    return chipsContainer;
  };

  const chipsContainerValues = prepareChipsContainer(myFilters);

  const handleChange = (filterName: string, value: (string | number)[] | string | number): void => {
    const isArrayOfNumbers = Array.isArray(value) && value.every(Number.isInteger) && value.length > 0;
    if (isArrayOfNumbers) return;
    const newValue = checkFilterValue(value as string[] | string | number, filterName);

    dispatch(
      updateFilter({
        name: filterName,
        newValue,
      }),
    );
    econdaClickEvent(pathname, 'results filter', `${url.sectionName}`, `${value}`);
  };

  useEffect(() => {
    const fetchData = async () => await dispatch(fetchResults(myFilters));
    fetchData();
  }, [dispatch, myFilters]);

  const listOfShockAbsorbers = shockAbsorbers.map((partnumber) => partnumber.partNumber);

  usePricing(new RequestHandler());

  const pricing = useAppSelector((state) => state.pricing);

  return (
    <>
      <div className="sas-results-container container-fluid">
        <div className="result-page-type-of-motion-title">{hasFoXHeadline ? t(headlineName) : null}</div>
        <div className="filter-and-result-container">
          <div className="facet-form-container">
            <FacetFilter
              title={t(TranslationKeys.FILTERS_TITLE)}
              className="ems-facet-filter-appication-endpoint"
              buttonText={t(TranslationKeys.SHOW_RESULTS)}
            >
              <ResultsFilter onChange={handleChange} />
            </FacetFilter>
            <ChipContainer values={chipsContainerValues} />
            <InfoPanel />
          </div>

          <div className="results-container">
            <>
              {isLoading === 'pending' && (
                <div className="results-container__loading-indicator">
                  <LoadingIndicator size="large" />
                </div>
              )}
              {isLoading === 'success' && (shockAbsorbers.length === 0 || !shockAbsorbers) && <NotFound />}

              {isLoading === 'success' &&
                shockAbsorbers &&
                shockAbsorbers.map((shockAbsorber, index) => (
                  <Fragment key={`${shockAbsorber.orderCode} ${shockAbsorber.partNumber}`}>
                    <div className="fwe-d-flex fwe-align-items-center">
                      {shockAbsorber.index === 1 ? (
                        <div className="solution-tile__most-economic-solution">
                          <h3 className="fr-heading">{`${t(TranslationKeys.SOLUTION)} ${index + 1}`}</h3>
                          {/* <Badge /> */}
                        </div>
                      ) : (
                        <h3 className="fr-heading">{`${t(TranslationKeys.SOLUTION)} ${index + 1}`}</h3>
                      )}
                    </div>
                    <SolutionTile
                      key={shockAbsorber.orderCode}
                      shockAbsorber={shockAbsorber}
                      articleCodes={listOfShockAbsorbers}
                      price={pricing?.values?.[index] ?? null}
                    />
                  </Fragment>
                ))}
              {isLoading === 'error' && <h3>Something went wrong, please change the filters on the previous page</h3>}
            </>
          </div>
        </div>
      </div>
    </>
  );
};

export default Results;
